<template>
  <div class="zoox-component zoox-sidebar-template">
    <div class="header">
      <slot name="title">
        <h3 class="template-title">{{ title }}</h3>
      </slot>

      <div class="template-actions">
        <!-- @slot Onde são colocadas as ações -->
        <slot name="actions"></slot>
      </div>
    </div>

    <div class="wrapper">
      <transition v-if="leftSidebar" name="show-left">
        <z-card
          v-if="isSidebarOpened"
          class="template-main-sidebar mr-3"
          :title="sidebarTitle"
          :width="sidebarWidth"
        >
          <template #content>
            <!--@slot Slot de conteúdo da sidebar -->
            <slot name="sidebar"></slot>
          </template>
        </z-card>
      </transition>

      <div class="main-content" :style="{width: mainContentWidth}">
        <div class="template-main-content" :style="mainContentStyle">
          <!-- @slot Onde é colocado o conteúdo principal -->
          <slot name="main-content"></slot>
        </div>
      </div>

      <transition
        v-if="!leftSidebar"
        :style="{width: sidebarWidth + 'px'}"
        name="show"
      >
        <z-card
          v-if="isSidebarOpened"
          class="template-main-sidebar ml-3"
          :title="sidebarTitle"
          :width="sidebarWidth"
        >
          <template #actions>
            <slot name="sidebar-actions"></slot>
          </template>
          <template #content>
            <slot name="sidebar"></slot>
          </template>
        </z-card>
      </transition>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash.debounce'

/**
 * Componente de template de página responsável
 * por renderizar uma página que contém um conteúdo principal
 * e uma sidebar lateral.
 *
 * Recomendado a utilização em dashboards
 * @displayName Sidebar Template
 */
export default {
  name: 'ZSidebarTemplate',
  props: {
    /**
     * Título da página
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Flag para indicar que o sidebar está aberto
     */
    isSidebarOpened: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para indicar que a sidebar aparecerá na esquerda
     */
    leftSidebar: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Props repassadas para o z-card do sidebar
     */
    sidebarTitle: {
      type: String,
    },
    /**
     * Flag para indicar que o sidebar
     * deve ser grande. (300px / 500px)
     */
    sidebarWidth: {
      type: Number,
      default: 300,
    },
    /**
     * @private
     */
    relative: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    mainContentWidth() {
      return this.isSidebarOpened
        ? `calc(100% - ${this.sidebarWidth + 12}px)`
        : '100%'
    },
    mainContentStyle() {
      if (this.relative) {
        return {width: '100%'}
      }

      return this.isSidebarOpened
        ? {width: `calc(100vw - ${this.sidebarWidth + 97 + 15}px)`}
        : {width: `calc(100vw - 97px)`}
    },
  },

  watch: {
    isSidebarOpened: debounce(() => {
      window.dispatchEvent(new Event('resize'))
    }, 500),
  },
}
</script>

<style lang="scss" scoped>
.zoox-sidebar-template {
  height: calc(100vh - 48px);
  width: 100%;
  padding: 15px;
}

.wrapper {
  display: flex;
  width: 100%;
  height: calc(100% - 40px);
  margin-top: 12px;
}

.header {
  display: flex;
  justify-content: space-between;
}

.main-content {
  display: grid;
  grid-template-rows: 1fr;
  height: 100%;
  width: 100%;

  .template-main-content {
    transition: width 500ms linear;
    height: 100%;
  }
}

.template-title {
  h3 {
    font-weight: 500 !important;
  }
}

.template-actions {
  justify-self: flex-end;
  column-gap: 15px;
}

.show {
  &-enter,
  &-leave-to {
    transform: translateX(100%);
    transition: all 500ms ease-in 0s;
  }

  &-enter-active,
  &-leave-active {
    transition: transform 0.5s ease;
  }
}

.show-left {
  &-enter,
  &-leave-to {
    transform: translateX(-100%);
    transition: all 500ms ease-in 0s;
  }

  &-enter-active,
  &-leave-active {
    transition: transform 0.5s ease;
  }
}
</style>
